import React, { useState } from 'react';

import PropTypes from 'prop-types';
import _ from 'lodash';
import { Notification, Spinner, Button } from '@telus-uds/components-web';

import request from '../../../../utils/request';
import {
  formDataKeyMapping,
  getDefectAssignee,
  initialFieldData
} from '../util';
import { useLocalizedStringsContext } from '../../../../context/LocalizedStringsContext';

const SubmitButton = ({
  fieldData,
  setFieldData,
  formErrors,
  setFormErrors
}) => {
  const localizedString = useLocalizedStringsContext();
  const {
    reportAnIssue: {
      form: { buttonSubmit }
    },
    DEFECT_MANAGEMENT: { STATUS_OPTIONS }
  } = localizedString;

  const [loading, setLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const isValid = () => {
    const errors = Object.entries(fieldData).reduce((acc, [field, value]) => {
      if (field !== 'attachment' && field !== 'webformOrderID' && !value) {
        acc[field] = { message: 'Field is required' };
        return acc;
      }
      if (
        ['issueSummary', 'errorMessage'].includes(field) &&
        value.length > 200
      ) {
        acc[field] = { message: 'Maximum 200 characters allowed' };
        return acc;
      }
      if (field === 'stepsToReplicate' && value.length > 500) {
        acc[field] = { message: 'Maximum 500 characters allowed' };
        return acc;
      }

      return acc;
    }, {});
    setFormErrors(errors);
    return _.isEmpty(errors);
  };

  const handleSubmit = async () => {
    if (isValid() && _.isEmpty(formErrors)) {
      setSubmitStatus('');
      setLoading(true);
      try {
        const formData = new FormData();
        const { id, name, email } = request.getProfile();

        /** APPENDING USER INFO AND INITIAL STATUS */
        formData.append('created_by', id);
        formData.append('name', name);
        formData.append('email', email);
        formData.append('status', STATUS_OPTIONS[0]);

        /** APPENDING FORM FIELDS */
        Object.entries(fieldData).forEach(([field, value]) => {
          formData.append(formDataKeyMapping[field], value);
        });

        /** APPENDING ASSIGNEE AND UPDATE_FROM_ADMIN FLAG  */
        const { assignee, adminUpdate } = getDefectAssignee(
          fieldData,
          localizedString,
          id
        );
        formData.append('assigned_user_id', assignee);
        formData.append('new_update_for_admin', adminUpdate);

        const result = await request.post('/defects', formData);
        if (result) {
          setSubmitStatus('success');
          setFieldData(initialFieldData);
          setLoading(false);
        }
      } catch (err) {
        setLoading(false);
        setSubmitStatus('error');
        throw err;
      }
    }
  };

  return (
    <div>
      {['error', 'success'].includes(submitStatus) && (
        <Notification 
          variant={submitStatus === 'error' ? { validation: 'error' } : { style: 'success' }}
          dismissible
        >
          {submitStatus === 'error'
            ? 'Error occurred'
            : 'Thank you for reporting an issue, we have received your request. We will review and respond as soon as we`re able to.'}
        </Notification>
      )}
      <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
        <Spinner show={loading} variant={{ size: 'small' }} label="updating..." inline>
          <Button onPress={handleSubmit}>{buttonSubmit}</Button>
        </Spinner>
      </div>
    </div>
  );
};

SubmitButton.propTypes = {
  fieldData: PropTypes.shape({
    defectWebform: PropTypes.string,
    defectCategory: PropTypes.string,
    priority: PropTypes.string,
    issueSummary: PropTypes.string,
    errorMessage: PropTypes.string,
    stepsToReplicate: PropTypes.string,
    attachment: PropTypes.string
  }).isRequired,
  setFieldData: PropTypes.func.isRequired,
  formErrors: PropTypes.shape({
    defectCategory: PropTypes.shape({
      message: PropTypes.string
    })
  }).isRequired,
  setFormErrors: PropTypes.func.isRequired
};

export default SubmitButton;
